/**
 * Hook over the tenantStats Redux slice. Provides counts used by the
 * CRM sidebar's badge numbers (prospects, customers, contacts,
 * resellers, leads, tasks, overdue invoices). Auto-refreshes every
 * 30 s by default.
 *
 * ⚠ Fetching here is BOUNDED, deliberately — at most one request per mount
 * plus one per (backed-off) poll tick. The first version gated the mount
 * fetch on `!stats && !loading` with `loading` in the dependency array, so
 * every rejection flipped `loading` back to false, re-satisfied the
 * condition, and re-dispatched immediately: a failing endpoint became an
 * unbounded request loop running at network speed. On 2026-08-24 one
 * desktop client sent ~500 requests in 32 seconds, tripped crm-api's
 * rack-attack fail2ban, and got its whole IP 403'd ("Access denied.") off
 * the CRM for a day. Keep the attempt bookkeeping in refs, never in the
 * effect's own condition. Pinned by useTenantStats.retry.test.tsx.
 */
import { useEffect, useCallback, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../lib/store'
import {
  fetchTenantStats,
  retryBackoffMs,
  selectTenantStats,
  selectTenantStatsLoading,
  selectTenantStatsError,
  selectFailureCount,
  selectLastFailedAt,
  selectLastFetched,
  selectAutoRefresh,
  clearError,
  forceRefresh,
  type TenantStats,
} from '../lib/store/tenantStatsSlice'

interface UseTenantStatsOptions {
  autoRefresh?: boolean
  refreshInterval?: number // in milliseconds
  fetchOnMount?: boolean
}

export interface SidebarCounts {
  prospects: number
  customers: number
  contacts: number
  resellers: number
  /** All leads (regular + AI). Kept for back-compat; the Prospects badge now
   * uses `regularProspects` so it matches that page's own list + stats. */
  leads: number
  /** Regular (human-entered) prospects — drives the "Prospects" badge. */
  regularProspects: number
  /** AI-generated prospects — drives the "AI Prospects" badge. */
  aiProspects: number
  tasks: number
  activities: number
  overdueInvoices: number
}

/**
 * Map a raw {@link TenantStats} payload to the count badges the CRM sidebar
 * renders. Pure + exported so it can be unit-tested without the Redux/HTTP
 * machinery. Per-origin prospect counts fall back gracefully: a brain that
 * predates the `ai_prospects` / `regular_prospects` fields reports the AI badge
 * as 0 and the Prospects badge as the all-leads total (never blank).
 */
export function deriveSidebarCounts(stats: TenantStats | null): SidebarCounts | null {
  if (!stats) return null
  return {
    prospects: stats.prospects,
    customers: stats.customers,
    contacts: stats.contacts || 0,
    resellers: stats.resellers || 0,
    leads: stats.totals?.leads || 0,
    regularProspects: stats.regular_prospects ?? stats.totals?.regular_leads ?? stats.totals?.leads ?? 0,
    aiProspects: stats.ai_prospects ?? stats.totals?.ai_leads ?? 0,
    tasks: stats.task_stats?.total || 0,
    activities: stats.recent_activity?.activities || 0,
    overdueInvoices: stats.overdue_invoices || stats.unpaid_invoices || 0,
  }
}

export const useTenantStats = (options: UseTenantStatsOptions = {}) => {
  const {
    autoRefresh = true,
    refreshInterval = 30000, // 30 seconds
    fetchOnMount = true
  } = options

  const dispatch = useAppDispatch()
  const stats = useAppSelector(selectTenantStats)
  const loading = useAppSelector(selectTenantStatsLoading)
  const error = useAppSelector(selectTenantStatsError)
  const lastFetched = useAppSelector(selectLastFetched)
  const autoRefreshEnabled = useAppSelector(selectAutoRefresh)
  const failureCount = useAppSelector(selectFailureCount)
  const lastFailedAt = useAppSelector(selectLastFailedAt)

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  // True once this mount has dispatched its own initial fetch. A REF, not
  // state: it must not participate in the effect's dependencies, or we are
  // back to the re-entrant loop described in the module header.
  const didFetchOnMountRef = useRef(false)
  // Earliest epoch-ms at which the POLLER may fire again. 0 = no backoff.
  // Mirrored into a ref so the interval callback reads the current value
  // without the interval having to be torn down and rebuilt on every failure.
  const retryNotBeforeRef = useRef(0)

  useEffect(() => {
    retryNotBeforeRef.current =
      failureCount > 0 && lastFailedAt !== null
        ? lastFailedAt + retryBackoffMs(failureCount)
        : 0
  }, [failureCount, lastFailedAt])

  const fetchStats = useCallback(() => {
    dispatch(fetchTenantStats())
  }, [dispatch])

  const clearStatsError = useCallback(() => {
    dispatch(clearError())
  }, [dispatch])

  useEffect(() => {
    if (!(autoRefresh && autoRefreshEnabled && refreshInterval > 0)) return

    refreshIntervalRef.current = setInterval(() => {
      // Hold off while the backoff window from the last failure is open.
      // The badges are a nicety; hammering a failing endpoint is not.
      if (Date.now() < retryNotBeforeRef.current) return
      dispatch(fetchTenantStats())
    }, refreshInterval)

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [autoRefresh, autoRefreshEnabled, refreshInterval, dispatch])

  useEffect(() => {
    // Exactly ONE initial fetch per mount, whatever the outcome. Retrying a
    // failure is the poller's job (with backoff) or the user's (forceRefresh).
    if (!fetchOnMount || didFetchOnMountRef.current) return
    didFetchOnMountRef.current = true
    dispatch(fetchTenantStats())
  }, [fetchOnMount, dispatch])

  // Explicit, human-initiated refresh — always allowed, backoff or not, and
  // it clears the backoff so normal polling resumes from a clean slate.
  const forceRefreshStats = useCallback(() => {
    retryNotBeforeRef.current = 0
    dispatch(forceRefresh())
    dispatch(fetchTenantStats())
  }, [dispatch])

  return {
    stats,
    loading,
    error,
    lastFetched,

    fetchStats,
    clearError: clearStatsError,
    forceRefresh: forceRefreshStats,

    // Individual counts for easy access by sidebar badges + similar.
    counts: deriveSidebarCounts(stats)
  }
}
